import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  PresidioDiscoveryAccessRequest,
  PresidioDiscoveryAddressMeta,
  PresidioDiscoveryAffiliate,
  PresidioDiscoveryPurchaseRule,
  PresidioDiscoverySkuOption,
  PresidioDiscoveryUser,
} from '../models/presidio-discovery.model';
import { PresidioDiscoveryService } from '../services/presidio-discovery.service';
import type { PresidioEmailDomainValidationResult } from '../utils/presidio-approved-email-domains';

/**
 * DEMO-ONLY facade for discovery prototype pages (sessionStorage mock).
 */
@Injectable({ providedIn: 'root' })
export class PresidioDiscoveryFacade {
  private readonly refresh$ = new BehaviorSubject<void>(undefined);

  constructor(private discoveryService: PresidioDiscoveryService) {}

  users$: Observable<PresidioDiscoveryUser[]> = this.refresh$.pipe(map(() => this.discoveryService.getUsers()));

  /** Logged-in discovery user for My Account role display. */
  sessionUser$: Observable<PresidioDiscoveryUser | undefined> = this.refresh$.pipe(
    map(() => this.discoveryService.getSessionUser())
  );

  getSessionUser(): PresidioDiscoveryUser | undefined {
    return this.discoveryService.getSessionUser();
  }

  /** Assigned affiliates for the session user — My Account filter dropdowns. */
  sessionAssignedAffiliates$: Observable<PresidioDiscoveryAffiliate[]> = this.refresh$.pipe(
    map(() => this.discoveryService.getSessionAssignedAffiliates())
  );

  accessRequests$: Observable<PresidioDiscoveryAccessRequest[]> = this.refresh$.pipe(
    map(() => this.discoveryService.getAccessRequests())
  );

  /** Combined pending purchase + user-access + Submitted quote approvals for Approvals nav badge. */
  pendingApprovalsCount$: Observable<number> = this.refresh$.pipe(
    map(() => this.discoveryService.getPendingApprovalsCount())
  );

  addresses$: Observable<PresidioDiscoveryAddressMeta[]> = this.refresh$.pipe(
    map(() => this.discoveryService.getAddressesForActiveCustomer())
  );

  assignableAffiliates(): PresidioDiscoveryAffiliate[] {
    return this.discoveryService.getAssignableAffiliates();
  }

  approvedEmailDomainsForAssignableAffiliates(): {
    affiliate: PresidioDiscoveryAffiliate;
    domains: string[];
  }[] {
    return this.discoveryService.getApprovedEmailDomainsForAssignableAffiliates();
  }

  setApprovedEmailDomains(customerNo: string, domains: string[] | string) {
    const notices = this.discoveryService.setApprovedEmailDomains(customerNo, domains);
    this.refresh();
    return notices;
  }

  validateUserEmailForAffiliates(
    email: string,
    affiliateCustomerNos: string[]
  ): PresidioEmailDomainValidationResult {
    return this.discoveryService.validateUserEmailForAffiliates(email, affiliateCustomerNos);
  }

  getSessionAssignedAffiliates(): PresidioDiscoveryAffiliate[] {
    return this.discoveryService.getSessionAssignedAffiliates();
  }

  /** Active users across the session user's assigned affiliates (Requester filters). */
  getUsersAcrossSessionAssignedAffiliates(): PresidioDiscoveryUser[] {
    return this.discoveryService.getUsersAcrossSessionAssignedAffiliates();
  }

  approverCandidates(excludeLogin?: string): PresidioDiscoveryUser[] {
    return this.discoveryService.getApproverCandidates(excludeLogin);
  }

  purchaseRulesForUser(login: string): PresidioDiscoveryPurchaseRule[] {
    return this.discoveryService.getPurchaseRulesForUser(login);
  }

  /** True when the signed-in user has at least one purchase rule (buyer requisitions nav). */
  sessionUserHasPurchaseRules(): boolean {
    return this.discoveryService.sessionUserHasPurchaseRules();
  }

  sessionUserHasPurchaseRules$: Observable<boolean> = this.refresh$.pipe(
    map(() => this.discoveryService.sessionUserHasPurchaseRules())
  );

  affiliateLabel(customerNo: string): string {
    return this.discoveryService.affiliateLabel(customerNo);
  }

  searchSkus(query: string, exclude: string[] = []): PresidioDiscoverySkuOption[] {
    return this.discoveryService.searchSkus(query, exclude);
  }

  activeCustomerNo(): string {
    return this.discoveryService.getActiveCustomerNo();
  }

  /**
   * Persist a user. Returns false when blocked as the last Active administrator
   * for an assigned account (MVP 1.2.1 / FR18.1.9).
   */
  upsertUser(user: PresidioDiscoveryUser): boolean {
    const ok = this.discoveryService.upsertUser(user);
    if (ok) {
      this.refresh();
    }
    return ok;
  }

  cannotDeactivateLastAdmin(user: PresidioDiscoveryUser): boolean {
    return this.discoveryService.cannotDeactivateLastAdmin(user);
  }

  cannotDemoteLastAdmin(user: PresidioDiscoveryUser): boolean {
    return this.discoveryService.cannotDemoteLastAdmin(user);
  }

  setPurchaseRulesForUser(
    login: string,
    rules: Omit<PresidioDiscoveryPurchaseRule, 'id' | 'userLogin'>[]
  ): void {
    this.discoveryService.setPurchaseRulesForUser(login, rules);
    this.refresh();
  }

  approveAccessRequest(
    id: string,
    overlay?: Partial<
      Pick<
        PresidioDiscoveryUser,
        | 'affiliateCustomerNos'
        | 'primaryRole'
        | 'canApprove'
        | 'firstName'
        | 'lastName'
        | 'approverLogin'
        | 'department'
        | 'title'
        | 'phone'
      >
    >
  ): void {
    this.discoveryService.approveAccessRequest(id, overlay);
    this.refresh();
  }

  rejectAccessRequest(id: string, comments?: string): void {
    this.discoveryService.rejectAccessRequest(id, comments);
    this.refresh();
  }

  getUser(login: string): PresidioDiscoveryUser | undefined {
    return this.discoveryService.getUser(login);
  }

  getAccessRequest(id: string): PresidioDiscoveryAccessRequest | undefined {
    return this.discoveryService.getAccessRequest(id);
  }

  setAddressHidden(addressMetaId: string, hidden: boolean): void {
    this.discoveryService.setAddressHidden(addressMetaId, hidden);
    this.refresh();
  }

  getPendingApprovalsCount(): number {
    return this.discoveryService.getPendingApprovalsCount();
  }

  /** Checkout banner when the active cart exceeds the session user's purchase rules. */
  getCheckoutPurchaseRuleNotice(input: {
    subtotal: number;
    lines: { sku: string; unitPrice: number }[];
    /** FR10.10.2 — quote carts ignore purchase rules. */
    hasQuoteLines?: boolean;
  }): { triggered: boolean; reasons: string[]; approverDisplayName?: string } {
    return this.discoveryService.getCheckoutPurchaseRuleNotice(input);
  }

  refresh(): void {
    this.refresh$.next();
  }
}
